import {
  ProviderUnavailableError,
  type ProviderCallOptions,
  type ProviderJobHandle,
  type ProviderVideoJobState,
  type VideoGenRequest,
  type VideoProvider,
} from "@loumy/contracts";

// §7 `video`-Provider → Pixverse V6 (D49/D53). ECHTER Adapter (Feasibility-Gate D24
// geräumt — siehe PRD §13/D55):
//   (a) key-barer Greenscreen  → JA (prompt-getrieben; Alpha entsteht im server-GPU
//       chroma-key der Post-Process-Phase, D45 — Pixverse liefert KEIN natives Alpha).
//   (b) kommerzielle Lizenz    → OK (Output kommerziell nutzbar; reine API-Resale ist
//       das, was die Pixverse-ToS untersagt — Loumy verkauft ein PRODUKT, kein API-Relay).
//
// Dokumentierter Contract (docs.platform.pixverse.ai, verifiziert 2026-05-31):
//   Auth  : Header `API-KEY: <key>` + `Ai-trace-id: <pro Request EINZIGARTIG>`
//           (gleiche trace-id ⇒ keine neue Generierung — deshalb je Call frisch).
//   Upload: POST /openapi/v2/image/upload (form-data) → { Resp:{ img_id, img_url } }
//   Gen   : POST /openapi/v2/video/img/generate (JSON)  → { Resp:{ video_id } }
//   Poll  : GET  /openapi/v2/video/result/{video_id}    → status 1=fertig(url) · 5=läuft
//                                                          · 7=Moderation-Fail · 8=Gen-Fail
//   Dauer V6/c1 = 1~15s INTEGER (kein 5s-Floor, konsistent §10.1/D53); ältere Modelle
//   wären 5/8 diskret → der Adapter pinnt bewusst `model='v6'`. `quality` ∈ 360p/540p/
//   720p/1080p. Audio über `generate_audio_switch` (v6). KEIN fps/loop-Param: der Loop
//   entsteht bei loop=on via first==last-TRANSITION (D76/D107) — die dieser Direkt-Swap
//   NICHT implementiert (s. Warnung an generate()); Frame-Rate via frame-interpolate/pack.

const PIXVERSE_DEFAULT_BASE_URL = "https://app-api.pixverse.ai";
const PIXVERSE_MODEL = "v6"; // gepinnt: nur V6/c1 unterstützen freie 1~15s-Dauer
const QUALITIES = new Set(["360p", "540p", "720p", "1080p"]);
const MIN_DURATION_S = 1;
const MAX_DURATION_S = 15; // V6/c1 (ältere Modelle: 5/8) — siehe §13/D55

export interface PixverseConfig {
  apiKey?: string;
  baseUrl?: string;
  /** Optionaler Modell-Override (Default 'v6'); nur Modelle mit 1~15s-Dauer sind getestet. */
  model?: string;
  /** Pixverse motion_mode (Default 'normal'). */
  motionMode?: "normal" | "fast";
}

interface PixverseEnvelope<T> {
  ErrCode: number;
  ErrMsg?: string;
  Resp: T;
}

export class PixverseVideoProvider implements VideoProvider {
  readonly id = "pixverse";
  readonly version = 1;
  readonly displayName = "Pixverse V6 (I2V)";
  // V6-Limits (verifiziert §13/D55). resolutions ASCENDING — der Default wird in
  // buildPipeline NAMENTLICH gewählt (nicht über die Array-Position, D34-Determinismus).
  readonly limits = {
    maxDurationS: MAX_DURATION_S,
    maxFps: 24,
    supportsAudio: true,
    resolutions: ["360p", "540p", "720p", "1080p"],
  };

  private readonly baseUrl: string;
  private readonly model: string;
  private readonly motionMode: "normal" | "fast";

  constructor(private readonly config: PixverseConfig = {}) {
    this.baseUrl = (config.baseUrl ?? PIXVERSE_DEFAULT_BASE_URL).replace(/\/+$/, "");
    this.model = config.model ?? PIXVERSE_MODEL;
    this.motionMode = config.motionMode ?? "normal";
  }

  private requireKey(): string {
    if (!this.config.apiKey)
      throw new ProviderUnavailableError("video", this.id, "PIXVERSE_API_KEY fehlt (Adapter nicht konfiguriert, §13).");
    return this.config.apiKey;
  }

  /** API-KEY + frische Ai-trace-id (Pixverse verlangt je Request eine einzigartige ID). */
  private headers(apiKey: string, json: boolean): Record<string, string> {
    const h: Record<string, string> = { "API-KEY": apiKey, "Ai-trace-id": crypto.randomUUID() };
    if (json) h["Content-Type"] = "application/json";
    return h;
  }

  private async unwrap<T>(res: Response, op: string): Promise<T> {
    if (!res.ok) throw new Error(`Pixverse ${op}: HTTP ${res.status} ${res.statusText}`);
    const body = (await res.json()) as PixverseEnvelope<T>;
    if (body.ErrCode !== 0) throw new Error(`Pixverse ${op}: ErrCode ${body.ErrCode} ${body.ErrMsg ?? ""}`.trim());
    return body.Resp;
  }

  /** Lädt das Start-Frame (R2-URL) zu Pixverse hoch → img_id (I2V-Voraussetzung). */
  private async uploadStartFrame(apiKey: string, url: string, signal?: AbortSignal): Promise<number> {
    const imgRes = await fetch(url, { signal });
    if (!imgRes.ok) throw new Error(`Pixverse upload: Start-Frame nicht ladbar (HTTP ${imgRes.status})`);
    const blob = await imgRes.blob();
    const form = new FormData();
    form.append("image", blob, "start-frame");
    const res = await fetch(`${this.baseUrl}/openapi/v2/image/upload`, {
      method: "POST",
      headers: this.headers(apiKey, false), // kein Content-Type: fetch setzt multipart-boundary selbst
      body: form,
      signal,
    });
    const resp = await this.unwrap<{ img_id: number }>(res, "upload");
    return resp.img_id;
  }

  // ⚠️ D107/§13: dieser Direkt-Swap implementiert den TRANSITION-Endpoint NICHT — `endFrameUrl`
  // wird ignoriert (reines i2v). Seit D107 betrifft das BEIDE Loop-Pfade: loop=on (first==last-
  // FLF, D76/D107) UND die gerichtete loop=off-Transition (D91). Wird dieser Adapter Default
  // (statt fal.ts), MUSS die Transition nachgezogen werden, sonst loopen die Videos nicht.
  async generate(req: VideoGenRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle> {
    const apiKey = this.requireKey();
    if (!req.startFrameUrl)
      throw new ProviderUnavailableError("video", this.id, "Pixverse I2V braucht ein Start-Frame (startFrameUrl fehlt).");

    const quality = req.resolution ?? "540p";
    if (!QUALITIES.has(quality)) throw new Error(`Pixverse: unbekannte quality '${quality}' (∈ 360p/540p/720p/1080p).`);
    // V6 nimmt nur GANZE Sekunden 1~15 — D49-Perioden (run 0.7, jump 0.8) runden auf 1s.
    const duration = Math.max(MIN_DURATION_S, Math.min(MAX_DURATION_S, Math.round(req.durationS)));

    const img_id = await this.uploadStartFrame(apiKey, req.startFrameUrl, opts?.signal);

    // Handle VOR Persist sichern (D27): erst generate-Call, dann gibt der Runner persistiert.
    const res = await fetch(`${this.baseUrl}/openapi/v2/video/img/generate`, {
      method: "POST",
      headers: this.headers(apiKey, true),
      body: JSON.stringify({
        img_id,
        model: this.model,
        prompt: req.prompt,
        quality,
        duration,
        motion_mode: this.motionMode,
        generate_audio_switch: req.audio === true, // v6 (D53: Audio = eigener CostTerm)
      }),
      signal: opts?.signal,
    });
    const resp = await this.unwrap<{ video_id: number }>(res, "generate");

    return {
      providerId: this.id,
      providerVersion: this.version,
      externalId: String(resp.video_id),
      // Provenance-Bausteine für poll() (D39): Pixverse liefert keinen Seed zurück.
      raw: { model: this.model, quality, duration, audio: req.audio === true, seed: null },
    };
  }

  async poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderVideoJobState> {
    const apiKey = this.requireKey();
    const res = await fetch(`${this.baseUrl}/openapi/v2/video/result/${handle.externalId}`, {
      method: "GET",
      headers: this.headers(apiKey, false),
      signal: opts?.signal,
    });
    const resp = await this.unwrap<{ status: number; url?: string }>(res, "result");
    const meta = (handle.raw ?? {}) as { model?: string; seed?: number | null };

    switch (resp.status) {
      case 1: {
        if (!resp.url) return { status: "failed", error: "Pixverse: status=fertig, aber keine URL." };
        return {
          status: "done",
          output: {
            videoUrl: resp.url,
            // Pixverse bettet Audio in den Video-Container — kein separater Track (Demux =
            // video-gen-Capability, §13). audioUrl bleibt null.
            audioUrl: null,
            provenance: {
              provider: this.id,
              modelVersion: meta.model ?? this.model,
              requestId: handle.externalId,
              seed: meta.seed ?? null,
            },
          },
        };
      }
      case 5:
        return { status: "processing" };
      case 7:
        return { status: "failed", error: "Pixverse: Moderation fehlgeschlagen (status=7)." };
      case 8:
        return { status: "failed", error: "Pixverse: Generierung fehlgeschlagen (status=8)." };
      default:
        return { status: "processing", progress: undefined };
    }
  }
}
